import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { createSelector } from 'reselect';

export const getDashboard = (state) => get(state, 'dashboard.dashboard', {});
export const getPortletsMeta = (state) => get(state, 'dashboard.portlets_meta', {});
export const getUserDashboardAccess = (state) => get(state, 'user.dashboard_update_access', false);
export const getDashboardList = (state) => get(state, 'dashboard.list', {});

export const portletMetaSelector = createSelector(
  (state, id) => getPortletsMeta(state)[id],
  (portlet_meta) => portlet_meta
);

export const dashboardRawDataSelector = createSelector(
  getDashboard,
  ({ data }) => data || {},
);

export const dashboardEditDataSelector = createSelector(
  getDashboard,
  ({ edit }) => edit || {},
);

export const dashboardDataSelector = createSelector(
  getDashboard,
  ({ isEditMode, edit, data }) => (isEditMode ? edit : data) || {}
);

export const dashboardEditModeSelector = createSelector(
  getDashboard,
  ({ isEditMode }) => isEditMode
);

export const dashboardSaveSelector = createSelector(
  getDashboard,
  ({ isSaving, isSaveSuccess }) => ({ isSaving, isSaveSuccess })
);

export const dashboardCreateSelector = createSelector(
  getDashboard,
  ({ isCreate }) => isCreate
);

export const dashboardErrorsSelector = createSelector(
  getDashboard,
  ({ saveError, fetchError, selectError }) => ({ saveError, fetchError, selectError })
);

export const dashboardFetchingSelector = createSelector(
  getDashboard,
  ({ isFetching }) => isFetching
);

export const dashboardLayoutSelector = createSelector(
  dashboardDataSelector,
  ({ layout }) => layout || []
);

export const dashboardLayoutStringSelector = createSelector(
  dashboardLayoutSelector,
  (layout) => layout.join(',')
);

export const dashboardPortletsSelector = createSelector(
  dashboardDataSelector,
  ({ portlets }) => portlets || []
);

export const dashboardNewPortletsSelector = createSelector(
  dashboardDataSelector,
  ({ portlets }) =>
    (portlets || []).map((portlet) => {
      const { id, isNew } = portlet;
      portlet.tempId = id;
      portlet.id = isNew ? undefined : id;
      return portlet;
    })
);

export const dashboardPortletSelector = createSelector(
  [(state) => dashboardPortletsSelector(state), (_, id) => id],
  (portlets, id) => portlets.find(({ id: currId }) => id === currId)
);

export const dashboardPortletAutoRefreshSelector = createSelector(
  dashboardPortletSelector,
  ({ auto_refresh, auto_refresh_ms }) => ({ auto_refresh, auto_refresh_ms }),
);

export const dashboardPristineSelector = createSelector(
  getDashboard,
  ({ isEditMode, isCreate, edit, data, create }) => {
    if (isEditMode) {
      return isCreate ? isEqual(edit, create) : isEqual(edit, data);
    }

    return true;
  }
);

export const dashboardListDeleteSelector = createSelector(
  getDashboardList,
  ({ deleteError, deleteId, isDeleteSuccess, isDeleting }) => ({
    deleteError,
    deleteId,
    isDeleteSuccess,
    isDeleting,
  })
);

export const dashboardListFetchSelector = createSelector(
  getDashboardList,
  ({ isFetchSilent, isFetching, fetchError }) => ({
    fetchError,
    isFetching,
    isFetchSilent,
  })
);

export const dashboardListSelectSelector = createSelector(
  getDashboardList,
  ({ isSelecting, selectError }) => ({
    isSelecting,
    selectError,
  })
);

export const dashboardListTermSelector = createSelector(
  getDashboardList,
  ({ searchTerm }) => searchTerm || ''
);

export const dashboardListDataSelector = createSelector(
  getDashboardList,
  ({ list }) => list || [],
);

export const dashboardListDataPrivateSelector = createSelector(
  dashboardListDataSelector,
  (list) => list.filter(({ is_private }) => is_private),
);

export const dashboardListDataPublicSelector = createSelector(
  dashboardListDataSelector,
  (list) => list.filter(({ is_private }) => !is_private),
);

export const dashboardListDataSegmentSelector = createSelector(
  [dashboardListDataPrivateSelector, dashboardListDataPublicSelector],
  (privateList, publicList) => [privateList, publicList]
);

export const dashboardAutoRefreshSelector = createSelector(
  dashboardRawDataSelector,
  ({ auto_refresh, auto_refresh_ms }) => ({ auto_refresh, auto_refresh_ms })
);